/**
 * One list of the characters that are never legitimately part of text a person typed.
 *
 * It has two consumers that do very different things with it, which is exactly why it
 * lives on its own:
 *
 *   - `documentFilename.ts` (steward-app#525) repairs a filename down to something an
 *     operating system can be trusted with, and removes every one of these.
 *   - `displayText.ts` (steward-app#600) strips them out of the free text a member
 *     types — an account name, an entity name, a document title — so a stored name
 *     cannot display as something it is not.
 *
 * The rules are not the same rule, and they should not be forced into one function: a
 * filename that repairs to nothing becomes `document`, whereas a title that strips to
 * nothing is simply an empty title, and a newline is fatal in a filename and ordinary
 * in a note. What the two DO share is the answer to "which characters are never
 * legitimate here", and that answer had already been written down twice before this
 * module existed. A third copy is how the copies drift apart, so there is one.
 *
 * ## What is in the class, and why each part
 *
 * `\p{Cc}` — the control characters: NUL through U+001F, DEL, and the C1 range. Nothing
 * a person types into a name field belongs here. NUL truncates a C string, ESC drives a
 * terminal, and the C1 range is mostly a mojibake artefact of a bad decode.
 *
 * `\p{Cf}` — the format characters, and the reason this module exists at all. It holds
 * the bidi controls (U+202A–U+202E, U+2066–U+2069) and the directional marks (U+200E,
 * U+200F, U+061C). A RIGHT-TO-LEFT OVERRIDE reverses everything after it, so
 * `statement<RLO>fdp.exe` reads as `statementexe.pdf` on screen — the classic spoof,
 * and it works just as well on a document title as on a filename. The same class also
 * covers the soft hyphen, the word joiner, the zero-width joiners and a byte-order
 * mark that has drifted into the middle of a string, all of which are invisible and so
 * can make two different stored values look like the same one. Two of those zero-width
 * joiners are exempted from the DISPLAY form only, for the reason set out below.
 *
 * `\p{Cs}` — the surrogate range. In a well-formed JavaScript string these only appear
 * unpaired, and an unpaired surrogate cannot be encoded as UTF-8 at all, so it is a
 * value that breaks somewhere downstream rather than a character anybody meant.
 *
 * ## Where the display rule stops, and the cost of stopping there
 *
 * `\p{Cf}` taken whole is wider than the demonstrated threat, and two of its members are
 * characters people legitimately type into their own names. The display-text form
 * exempts exactly those two (steward-app#602):
 *
 *   - **U+200C ZERO WIDTH NON-JOINER** is orthographic, not decorative. Persian needs it
 *     to stop a prefix joining the word after it, and Arabic, Urdu and several Indic
 *     scripts use it the same way. Stripping it hands a family their own name back
 *     subtly misshapen, which is a quiet insult to the members this product most wants
 *     to serve well.
 *   - **U+200D ZERO WIDTH JOINER** is what holds a multi-part emoji together. Strip it
 *     and a woman-and-girl emoji comes apart into two separate people, and folder and
 *     entity names do carry emoji.
 *
 * Neither can reorder anything, and reordering is what the spoof needs. Both are bidi
 * class BN (boundary neutral), which UAX #9 rule X9 removes before the weak, neutral and
 * implicit rules run, so a BN cannot take part in resolving levels at all. The spoof
 * takes a bidi control (U+202A–U+202E, U+2066–U+2069) or a directional mark (U+200E,
 * U+200F, U+061C), and both forms still remove every one of those.
 *
 * That — not the `<bdi>` at the render sites — is what makes the exemption safe, and the
 * distinction matters for anyone tempted to widen it further. Two surfaces have no markup
 * to isolate with and are carried by this strip alone: an invite email's subject line and
 * the browser tab title, both named in `displayText.ts`. A character is only safe to
 * exempt here if it is safe THERE, and "the render site wraps it anyway" is not true of
 * either one.
 *
 * ## What the exemption gives up
 *
 * Confusability. Both characters are zero-width, so `Acme<ZWNJ>Trust` and `AcmeTrust` are
 * two stored values that look alike. Worse than mere padding: in Arabic-script text these
 * two are exactly the controls for cursive joining, so a ZWNJ splits a word that would
 * otherwise join and a ZWJ forces a medial form. They do not just hide, they RESHAPE the
 * letters either side of them. That is inseparable from honouring the orthography at all
 * — you cannot have the Persian name without it — but it is a stronger look-alike
 * primitive than "invisible" suggests, and the same property lets a long run of them
 * carry one bit per character into a log, a CSV export or the assistant's snapshot.
 *
 * Two knock-on effects, stated so they are not discovered:
 *
 *   - A value that is NOTHING BUT these two characters now survives the strip, so a
 *     `.trim().min(1)` downstream of it sees a non-empty string and a name can be stored
 *     that renders as blank. That hole is not new — every `\p{Mn}` mark, a lone combining
 *     accent or a variation selector, has always passed the same check for the same
 *     reason — but this widens it. steward-app#943.
 *   - A `.max(N)` counts these toward the length, so N is a bound on stored code points
 *     and not on visible ones. It always was, for the same `\p{Mn}` reason.
 *
 * Both are why the exemption is these two characters and NOT the category. The soft
 * hyphen, the word joiner, the zero-width space, a drifted byte-order mark and the whole
 * U+E0000 tag block are all just as invisible, carry no orthography anybody typed, and
 * all still go. The tag block in particular is the one that encodes ASCII directly, and
 * it stays closed.
 *
 * The FILENAME form keeps taking the whole category, and that asymmetry is the point.
 * A filename is a machine-facing label that has to survive a filesystem, nothing is
 * expressed by carrying a ZWNJ into one, and #525 made that trade there already. A name
 * a person typed for themselves is the opposite case: it is the thing the product shows
 * back to them.
 *
 * Both consumers repair rather than refuse, so for everything still in the class the
 * cost is a name quietly cleaned, never an edit a member cannot save.
 *
 * ## A hazard worth naming
 *
 * The exported patterns carry the `g` flag, because both consumers use them with
 * `String.replace`. A global regex used with `.test()` or `.exec()` carries `lastIndex`
 * from call to call and will answer differently on alternate calls, and these are
 * module-level constants shared across files, so that state would be shared too. Use
 * them ONLY with `String.replace` / `String.split`. Every call site in this package
 * does; if you need a predicate, compare the replaced string against the original the
 * way `documentFilenameProblem` does.
 */

/**
 * Every character that is never legitimate, with no exceptions. This is the filename
 * form: a filename is a single line by definition, so the whitespace controls go too.
 */
export const UNSAFE_CHARS = /[\p{Cc}\p{Cf}\p{Cs}]/gu;

/**
 * The same list, less the five characters a person can legitimately type: the three
 * whitespace controls (tab, carriage return, line feed), and the two zero-width joiners
 * U+200C and U+200D.
 *
 * This is the display-text form. The free-text attributes of a workspace record share
 * one bag — a `notes` and a `description` sit beside a `name` in the same
 * `attributes` object — and a sanitizer that walks that bag cannot tell which key is
 * meant to be one line. Removing a member's paragraph breaks silently and permanently,
 * so the sanitizer keeps the whitespace and lets the layout decide what to do with it.
 * Nothing is lost by keeping them: a newline is visible, and a visible character cannot
 * make a name display as a different name.
 *
 * ZWNJ and ZWJ are exempted for a different reason and at a real cost, both set out
 * under "Where the display rule stops" above. They stay in the filename form.
 */
export const UNSAFE_DISPLAY_CHARS = /(?![\n\r\t\u200C\u200D])[\p{Cc}\p{Cf}\p{Cs}]/gu;
